import React, { useEffect, useState } from 'react';
import { useTheme } from '@xstyled/styled-components';
import ChevronUp from './ChevronUp';
import ChevronDown from './ChevronDown';

const locales = ['en', 'cs'];

function LocaleButton ({ locale, onClick }) {
    const emoji = locale === 'cs' ? '🇨🇿' : '🇬🇧';

    return (
        <button
            onClick={onClick}
            type="button"
            role="menuitem"
            aria-label={`Switch to ${locale === 'cs' ? 'Czech' : 'English'}`}
            style={{
                fontSize: '1.125rem',
                cursor: 'pointer',
                padding: '4px 8px',
                userSelect: 'none',
                background: 'none',
                border: 'none',
                width: '100%',
                textAlign: 'left'
            }}
        >
            {emoji}
        </button>
    );
}

function LanguageSwitch () {
    const [locale, setLocale] = useState('');
    const [open, setOpen] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        const path = window.location.pathname;
        const port = window.location.port;

        // Dev mode: detect by port (8000=cs, 8001=en)
        if (port === '8000') {
            setLocale('cs');
        } else if (port === '8001') {
            setLocale('en');
        } else {
            // Prod mode: detect by path prefix (/cs/... or /en/...)
            const match = path.match(/^\/(cs|en)(\/|$)/);
            setLocale(match ? match[1] : 'en');
        }
    }, []);

    const handleClickOutside = (e) => {
        if (
            open
            && !(document.getElementById('language-selector')?.contains(e.target))
        ) {
            setOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [open]);

    if (!locale) return null;

    const getHrefForLocale = (targetLocale) => {
        const { pathname, port, protocol, hostname } = window.location;

        // Dev mode: switch ports (8000=cs, 8001=en)
        if (port === '8000' || port === '8001') {
            const targetPort = targetLocale === 'cs' ? '8000' : '8001';
            return `${protocol}//${hostname}:${targetPort}${pathname}`;
        }

        // Prod mode: switch path prefix (/cs/... <-> /en/...)
        return pathname.replace(/^\/(en|cs)(\/|$)/, `/${targetLocale}$2`);
    };

    const containerMaxHeight = 48;
    const { languageSelectorBg } = theme.colors;

    return (
        <div
            id="language-selector"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls="language-options"
            tabIndex={0}
            onClick={(e) => {
                if (!open) {
                    setOpen(true);
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    setOpen(!open);
                    e.preventDefault();
                }
            }}
            style={{
                background: open ? languageSelectorBg : 'transparent',
                borderRadius: open ? '0.5rem 0.5rem 0 0' : '0.5rem',
                cursor: 'pointer',
                maxHeight: containerMaxHeight,
                overflow: 'visible',
                position: 'relative',
                display: 'inline-block'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', height: 32 }}>
                <LocaleButton locale={locale} />
                <button
                    type="button"
                    aria-label={open ? 'Close language menu' : 'Open language menu'}
                    aria-controls="language-options"
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.3rem',
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        color: languageSelectorBg
                    }}
                >
                    {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
            </div>
            <div
                id="language-options"
                role="listbox"
                aria-hidden={!open}
                style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    background: languageSelectorBg,
                    borderRadius: '0 0 0.5rem 0.5rem',
                    width: '100%',
                    display: open ? 'block' : 'none',
                    zIndex: 1000
                }}
            >
                {locales
                    .filter((l) => l !== locale)
                    .map((l) => (
                        <a
                            key={l}
                            href={getHrefForLocale(l)}
                            role="menuitem"
                            aria-label={`Switch to ${l === 'cs' ? 'Czech' : 'English'}`}
                            style={{
                                textDecoration: 'none',
                                display: 'block',
                                fontSize: '1.125rem',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                userSelect: 'none'
                            }}
                        >
                            {l === 'cs' ? '🇨🇿' : '🇬🇧'}
                        </a>
                    ))}
            </div>
        </div>
    );
}

export default LanguageSwitch;
